import { useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface AirportOffer {
  image?: any;
  name?: string;
  short_description?: string;
  offer_code?: string;
  formatted_price?: string;
  miles_and_more?: string;
  type?: string;
}

interface AirportOffersListProps {
  offers: AirportOffer[];
  transportSearchByType: Record<string, { isLoading?: boolean; isSuccess?: boolean; error?: string | null }>;
  areOffersEnabled: boolean;
  onOfferClick: (offer: AirportOffer) => void;
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "calc(100% - 32px)",
  maxWidth: 360,
  backgroundColor: "#fff",
  border: "1px solid #d9d9d9",
  boxShadow: 24,
  p: 2
} as const;

export default function AirportOffersList({
  offers,
  transportSearchByType,
  areOffersEnabled,
  onOfferClick
}: AirportOffersListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const supportsHover = useMediaQuery("(hover: hover)");
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isHoverTooltip = supportsHover && isDesktop;

  const [selectedOffer, setSelectedOffer] = useState<AirportOffer | null>(null);

  return (
    <Box
      data-cy="airline-offers"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5
      }}
    >
      <Box
        sx={{
          fontSize: 14,
          textTransform: "uppercase",
          fontFamily: "Neutraface2Demi",
          color: "#3a3a3a"
        }}
      >
        {t("airline.offers.title")}
      </Box>

      {offers.map((offer, index) => {
        const typeState = offer?.type ? transportSearchByType?.[offer.type] : null;
        const isTypeLoading = Boolean(typeState?.isLoading);
        const isTypeSuccess = Boolean(typeState?.isSuccess);
        const hasTypeError = Boolean(typeState?.error);
        const cardIsReady = areOffersEnabled && isTypeSuccess;
        const offerKey = offer?.type ? String(offer.type) : String(index);

        return (
          <Box
            key={offer.type != null ? `${offer.type}-${index}` : `offer-${index}`}
            onClick={() => cardIsReady && onOfferClick(offer)}
            data-cy={`airline-card-${offerKey}`}
            sx={{
              backgroundColor: "#fff",
              border: "1px solid #d9d9d9",
              minHeight: { xs: 92, md: 100 },
              px: { xs: 1, sm: 1.5, md: 2 },
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              opacity: cardIsReady ? 1 : 0.4,
              cursor: cardIsReady ? "pointer" : "default"
            }}
          >
            <Box
              sx={{
                width: { xs: 95, sm: 105, md: 130, lg: 160 },
                minWidth: { xs: 95, sm: 105, md: 10, lg: 160 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {offer?.image?.url ? (
                <img
                  src={offer.image.url}
                  alt={offer.name || "offer-logo"}
                  style={{
                    maxWidth: "100%",
                    objectFit: "contain"
                  }}
                />
              ) : null}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }} ml={{ xs: 1, md: 4 }} mr={{ xs: 1, md: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography
                  sx={{
                    fontFamily: "Neutraface2Demi",
                    fontSize: { xs: 18, md: 23 },
                    color: "#434343",
                    lineHeight: 1.05
                  }}
                >
                  {offer.name}
                </Typography>

                {isHoverTooltip ? (
                  <Tooltip title={offer.short_description || ""} arrow placement="top">
                    <IconButton
                      size="small"
                      sx={{ p: 0.25 }}
                      onClick={event => event.stopPropagation()}
                      data-cy={`airline-info-${offerKey}`}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#666" }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    data-cy={`airline-info-${offerKey}`}
                    onClick={event => {
                      event.stopPropagation();
                      setSelectedOffer(offer);
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "#666" }} />
                  </IconButton>
                )}
              </Box>

              {isTypeLoading ? (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: { xs: 12, md: 15 },
                    fontFamily: "Neutraface2Book",
                    color: "#222",
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <CircularProgress size={14} />
                  loading...
                </Typography>
              ) : cardIsReady ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 0, sm: 0.5 }
                  }}
                  mt={0.5}
                >
                  <Typography
                    sx={{ mt: 0.7, fontSize: { xs: 14, md: 16 }, fontFamily: "Neutraface2Demi", color: "#222" }}
                  >
                    {offer.formatted_price}
                  </Typography>

                  <Typography
                    sx={{ mt: 0.7, fontSize: { xs: 12, md: 15 }, fontFamily: "Neutraface2Book", color: "#222" }}
                  >
                    {offer.miles_and_more}
                  </Typography>
                </Box>
              ) : hasTypeError ? (
                <Typography
                  sx={{ mt: 0.7, fontSize: { xs: 12, md: 14 }, fontFamily: "Neutraface2Book", color: "#b00020" }}
                >
                  {t("search.error")}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}

      <Modal open={Boolean(selectedOffer)} onClose={() => setSelectedOffer(null)}>
        <Box sx={modalStyle} data-cy="airline-modal">
          <Typography sx={{ fontSize: 18, fontFamily: "Neutraface2Demi", mb: 1 }}>{selectedOffer?.name}</Typography>
          <Typography sx={{ fontSize: 16, fontFamily: "Neutraface2Book" }}>
            {selectedOffer?.short_description}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
}
